import { Link, useParams } from 'react-router-dom';
import MovieHero from './components/MovieHero';
import CastList from './components/CastList';
import MovieCard from '@/components/feature/MovieCard';
import SectionHeader from '@/components/feature/SectionHeader';
import EmptyState from '@/components/base/EmptyState';
import Tag from '@/components/base/Tag';
import { formatMeta, getActorsByIds, getMovieById, getRelatedMovies } from '@/lib/catalog';

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);

  if (!movie) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-film-line"
          title="未找到该影视作品"
          description="这件作品可能已下架或链接有误，返回首页看看其他推荐吧。"
          action={
            <Link
              to="/"
              className="rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  const castActors = getActorsByIds(movie.cast);
  const related = getRelatedMovies(movie, 4);
  const typeLabel = movie.type === 'movie' ? '电影' : '电视剧';

  const infoRows: { label: string; value: string }[] = [
    { label: '导演', value: movie.director },
    { label: '主演', value: castActors.map((a) => a.name).join('、') },
    { label: '类型', value: movie.genres.join(' / ') },
    { label: '上映时间', value: movie.releaseDate },
    { label: '片长 / 集数', value: formatMeta(movie) },
    { label: '地区', value: movie.region },
    { label: '评分', value: `${movie.rating.toFixed(1)} / 10` },
    { label: '状态', value: movie.status },
  ];

  return (
    <div className="w-full">
      <MovieHero movie={movie} />

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-6 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                剧情介绍
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                {movie.synopsis}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Tag key={genre} tone="accent">
                    {genre}
                  </Tag>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-6 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                演员列表
              </h2>
              <p className="mt-2 font-label text-xs text-foreground-500">
                点击演员可查看个人资料与代表作品
              </p>
              <div className="mt-5">
                <CastList actors={castActors} />
              </div>
            </section>
          </div>

          <aside>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">基本信息</h2>
              <dl className="mt-4 flex flex-col gap-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-20 flex-shrink-0 font-label text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 p-4">
              <p className="text-xs leading-relaxed text-foreground-600">
                <i className="ri-information-line mr-1 text-accent-500" aria-hidden="true" />
                收藏功能保存在本机浏览器中，无需登录即可使用；本站不提供在线播放，页面信息均为示范内容。
              </p>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <SectionHeader title="相关推荐" subtitle="根据类型与主演为你推荐" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 flex items-center justify-between rounded-lg border border-background-200 bg-secondary-50 px-5 py-4">
          <span className="font-label text-sm text-foreground-700">
            浏览更多{typeLabel}，继续发现好故事
          </span>
          <Link
            to={movie.type === 'movie' ? '/category/movie' : '/category/tv'}
            className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            返回{typeLabel}分类
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
}